import moment from 'moment';
import { ConfigConstants } from '../../config/config-constants';
import { NOTIFICATION_STATUS_CODE } from '../../enums/notification-status-code.enum';
import { NotificationState } from '../notification/notification-collection.state';
import { TravelStatusSubmission } from '../notification/notification-status.rules';
import { NotificationStateUtilities } from '../utilities/notification-state-utlities';
import { ODataTravelStatusUpdateOutModel, TravelStatusUpdateRequest } from './travel-status-update.types';
import { TravelStatusUpdateModel } from '../../models/complex/travel-status-update.model';

/**
 * Model <-> state conversions for the travel-status-update pipeline. Only
 * {@link travelStatusModelToRequest} touches a legacy model type, and only as a parameter type at
 * the `TravelStatusUpdateNgrxService.submitStatus` boundary — see architecture-rules.md's
 * "one exception" for a converter that has to cross that boundary. Every other function here
 * operates on the plain `TravelStatusUpdateRequest` state and the notification slice's state
 * types, so `TravelStatusUpdateEffects` never has to import a model.
 */

/**
 * The single model->state translation, called only at the service boundary.
 *
 * `start`/`end` come from `model.date.value` / `model.endDate.value` rather than
 * `model.startMoment` / `model.endMoment` — the model constructs its `DateModel`/`TimeModel`
 * around those very moment instances, so both pairs are the same objects.
 */
export function travelStatusModelToRequest(model: TravelStatusUpdateModel): TravelStatusUpdateRequest {
    return {
        notificationId: NotificationStateUtilities.toState(model.notification.identifier),
        nextStatus: model.nextStatus,
        start: model.date.value.toISOString(),
        end: model.endDate.value.toISOString(),
    };
}

/**
 * The deep-insert out model for a travel-status submit, byte-identical to
 * `TravelStatusUpdateModel.getOutModel()`: `Date`/`EndDate` use `formatDateNoTimezone`,
 * `Time`/`EndTime` use `formatXSDTimeDuration` — the defaults `DateModelOptions` and
 * `TimeModelOptions` apply on the model side. Mirrors `build-timesheet-out-model.ts`'s
 * `formatOdataDate`/`formatOdataTime`.
 *
 * @param request the request being submitted
 * @param entry the notification the request belongs to, for the identifier and time-zone fields
 */
export function requestToOutModel(
    request: TravelStatusUpdateRequest,
    entry: NotificationState,
): ODataTravelStatusUpdateOutModel {
    const start = moment(request.start);
    return {
        MessageType: request.nextStatus,
        Date: start.format(ConfigConstants.formatDateNoTimezone),
        Time: start.format(ConfigConstants.formatXSDTimeDuration),
        FrontId: entry.notificationId.frontId,
        NotificationNo: entry.notificationId.qmNum,
        TaskNumber: entry.notificationId.maNum,
        TimeZone: entry.timeZone.name,
        UTCSign: entry.timeZone.utcSign,
        UTCDiff: entry.timeZone.utcDiff,
    };
}

/**
 * What a travel-status request asks the notification slice to record. The statuses themselves are
 * derived from it by the rules in `notification-status.rules.ts`, which need the notification's DISP
 * window and recorded TRAV — so the reducer derives them, not the dispatch site.
 */
export function travelStatusSubmissionFor(request: TravelStatusUpdateRequest): TravelStatusSubmission {
    return { statusCode: request.nextStatus, start: request.start, end: request.end };
}

/**
 * The skipped-ETA sub-request. The legacy code built `new TravelStatusUpdateModel(notification,
 * ETA)`, whose constructor sets `startMoment = plannedStart` for ETA and `endMoment = startMoment +
 * plannedDuration` — i.e. `plannedEnd`.
 */
export function etaSubRequest(
    request: TravelStatusUpdateRequest,
    plannedStart: moment.Moment,
    plannedEnd: moment.Moment,
): TravelStatusUpdateRequest {
    return {
        ...request,
        nextStatus: NOTIFICATION_STATUS_CODE.ETA,
        start: plannedStart.toISOString(),
        end: plannedEnd.toISOString(),
    };
}

/**
 * The skipped-TRAV sub-request. The legacy code built the TRAV model and then ran
 * `changeStatusModelTimesForSkippingStatusChanges(travModel, travelStatusModel.startMoment)`,
 * which sets every time on the model to the main request's start.
 */
export function travSubRequest(request: TravelStatusUpdateRequest): TravelStatusUpdateRequest {
    return { ...request, nextStatus: NOTIFICATION_STATUS_CODE.TRAV, start: request.start, end: request.start };
}
